import { SearchFilterMediaTypes, SearchUrlParamKeys } from 'search-gateway-client';
import { SearchContentClass } from 'stockblocks-client/src/StockblocksClient';

import { audioContentTypes, genres, moods, musicInstruments, sfxCategories, vocalType } from './AudioFilterOptions';
import { ExtraAppliedFilterConfig, FilterConfig, FilterTypes } from './FilterTypes';
import {
    fileTypes,
    imageCategories,
    imageContentTypes,
    imageMediaDetails,
    orientations,
    usageRights,
} from './ImageFilterOptions';
import {
    footageCategories,
    frameRates,
    videoMediaDetails,
    motionBackgroundsCategories,
    qualities,
    templatesCategories,
    videoContentTypes,
} from './VideoFilterOptions';

const extraAudioFilters: ExtraAppliedFilterConfig[] = [
    {
        key: SearchUrlParamKeys.SEARCH_SIMILAR_ID,
        getValueLabel: (value: string) => `Tracks similar to: ${value}`,
    },
    {
        key: SearchUrlParamKeys.ARTIST_IDS,
        getValueLabel: (value: string) => value,
    },
];

const audioFilterConfig: FilterConfig[] = [
    {
        name: 'Media Type',
        key: SearchUrlParamKeys.MEDIA_TYPE,
        type: FilterTypes.RADIO_FILTER,
        options: audioContentTypes,
    },
    {
        name: 'Moods',
        key: 'categories',
        type: FilterTypes.CHECKBOX_FILTER,
        options: moods,
        enableCondition: (searchParams: URLSearchParams): boolean => {
            return searchParams.get(SearchUrlParamKeys.MEDIA_TYPE) === SearchFilterMediaTypes.Music;
        },
        showText: 'Select Music to see Moods',
    },
    {
        name: 'Genres',
        key: 'categories',
        type: FilterTypes.CHECKBOX_FILTER,
        options: genres,
        enableCondition: (searchParams: URLSearchParams): boolean => {
            return searchParams.get(SearchUrlParamKeys.MEDIA_TYPE) === SearchFilterMediaTypes.Music;
        },
        showText: 'Select Music to see Genres',
    },
    {
        name: 'Instruments',
        key: 'categories',
        type: FilterTypes.CHECKBOX_FILTER,
        options: musicInstruments,
        enableCondition: (searchParams: URLSearchParams): boolean => {
            return searchParams.get(SearchUrlParamKeys.MEDIA_TYPE) === SearchFilterMediaTypes.Music;
        },
        showText: 'Select Music to see Instruments',
    },
    {
        name: 'Vocals',
        key: 'vocals',
        type: FilterTypes.RADIO_FILTER,
        options: vocalType,
        enableCondition: (searchParams: URLSearchParams): boolean => {
            return searchParams.get(SearchUrlParamKeys.MEDIA_TYPE) === SearchFilterMediaTypes.Music;
        },
        showText: 'Select Music to see Vocals',
    },
    {
        name: 'Duration',
        key: 'duration',
        type: FilterTypes.DRAG_BAR_FILTER,
        options: [],
    },
    {
        name: 'Categories',
        key: 'categories',
        type: FilterTypes.CHECKBOX_FILTER,
        options: sfxCategories,
        enableCondition: (searchParams: URLSearchParams): boolean => {
            return searchParams.get(SearchUrlParamKeys.MEDIA_TYPE) === SearchFilterMediaTypes.Sound_effects;
        },
        showText: 'Select Sound Effects to see Categories',
    },
];

const videoFilterConfig: FilterConfig[] = [
    {
        name: 'Media Type',
        key: SearchUrlParamKeys.MEDIA_TYPE,
        type: FilterTypes.RADIO_FILTER,
        options: videoContentTypes,
    },
    {
        name: 'Resolution',
        key: 'resolution',
        type: FilterTypes.RADIO_FILTER,
        options: qualities,
    },
    {
        name: 'Frame Rate',
        key: 'frameRate',
        type: FilterTypes.CHECKBOX_FILTER,
        options: frameRates,
    },
    //add duration
    {
        name: 'Media Details',
        key: 'mediaDetails',
        type: FilterTypes.CHECKBOX_FILTER,
        options: videoMediaDetails,
    },
    {
        name: 'Usage Rights',
        key: 'usageRights',
        type: FilterTypes.RADIO_FILTER,
        options: usageRights,
    },
    {
        name: 'Footage Categories',
        key: 'categories',
        type: FilterTypes.CHECKBOX_FILTER,
        options: footageCategories,
        enableCondition: (searchParams: URLSearchParams): boolean => {
            return searchParams.get(SearchUrlParamKeys.MEDIA_TYPE) === 'Footage';
        },
        showText: 'Select Footage to see Categories',
    },
    {
        name: 'Templates Categories',
        key: 'categories',
        type: FilterTypes.CHECKBOX_FILTER,
        options: templatesCategories,
        enableCondition: (searchParams: URLSearchParams): boolean => {
            return searchParams.get(SearchUrlParamKeys.MEDIA_TYPE) === 'Templates';
        },
        showText: 'Select Templates to see Categories',
    },
    {
        name: 'Motion Backgrounds Categories',
        key: 'categories',
        type: FilterTypes.CHECKBOX_FILTER,
        options: motionBackgroundsCategories,
        enableCondition: (searchParams: URLSearchParams): boolean => {
            return searchParams.get(SearchUrlParamKeys.MEDIA_TYPE) === 'Backgrounds';
        },
        showText: 'Select Motion Backgrounds to see Categories',
    },
];
const imageFilterConfig: FilterConfig[] = [
    {
        name: 'Media Type',
        key: SearchUrlParamKeys.MEDIA_TYPE,
        type: FilterTypes.RADIO_FILTER,
        options: imageContentTypes,
    },
    {
        name: 'Orientation',
        key: 'orientation',
        type: FilterTypes.RADIO_FILTER,
        options: orientations,
    },
    //add Color filter
    {
        name: 'Media Details',
        key: 'mediaDetails',
        type: FilterTypes.CHECKBOX_FILTER,
        options: imageMediaDetails,
    },
    {
        name: 'Usage Rights',
        key: 'usageRights',
        type: FilterTypes.RADIO_FILTER,
        options: usageRights,
    },
    {
        name: 'Categories',
        key: 'categories',
        type: FilterTypes.CHECKBOX_FILTER,
        options: imageCategories,
        enableCondition: (searchParams: URLSearchParams): boolean => {
            return searchParams.get(SearchUrlParamKeys.MEDIA_TYPE) !== '';
        },
        showText: 'Select Images to see Categories',
    },
    {
        name: 'File Type',
        key: 'fileType',
        type: FilterTypes.CHECKBOX_FILTER,
        options: fileTypes,
    },
];

export const filterConfig: Record<SearchContentClass, FilterConfig[]> = {
    audio: audioFilterConfig,
    video: videoFilterConfig,
    images: imageFilterConfig,
};

// Filters that don't have filter menu options but are set from query params
export const extraAppliedFilters: Record<SearchContentClass, ExtraAppliedFilterConfig[]> = {
    audio: extraAudioFilters,
    video: [],
    images: [],
};
